import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  FreeDeliveryConfigurationV2,
  LogisticConfiguration,
  Partner,
} from "@dashboard/common/types";
import { actions, partnerSelector } from "@dashboard/config/redux";
import { fetchLastPremiumPlan } from "@dashboard/services/get-user-plan";
import { fetchUpdateUserDetailForDelivery } from "../fetchers/delivery-config.fetchers";
import { fetchEstimatedDeliveryTimes } from "../services/remote-config.service";
import { EstimatedDeliveryTimeElement } from "../entities/delivery.entities";
import { useDeliveryToken } from "./use-delivery-token.hook";
import { DELIVERY_ROUTES } from "../delivery.routes";
import {
  DeliverySettingsForm,
  mapPartnerToDeliverySettingsForm,
  mergeLogisticConfiguration,
  mergeUserDetailAfterSave,
} from "../utils/user-detail.utils";

export type { DeliverySettingsForm };

export const useDeliverySettings = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = useDeliveryToken();
  const partner = useSelector(partnerSelector);
  const [loading, setLoading] = useState(false);
  const [estimatedTimes, setEstimatedTimes] = useState<
    EstimatedDeliveryTimeElement[]
  >([]);
  const [form, setForm] = useState<DeliverySettingsForm>({
    customDeliveryEnabled: false,
    customDeliveryValue: 0,
    nationalLogisticEnabled: false,
    nationalDeliveryValue: 0,
    storeDelivery: false,
    homeDelivery: false,
    localFreeEnabled: false,
    localFreeMin: 0,
    nationalFreeEnabled: false,
    nationalFreeMin: 0,
    estimatedDeliveryTimeId: "",
    nationalEstimatedDeliveryTimeId: "",
  });

  const userId = partner?.userId;

  useEffect(() => {
    fetchEstimatedDeliveryTimes().then((data) => {
      setEstimatedTimes(data.estimatedDeliveryTimes || []);
    });
  }, []);

  useEffect(() => {
    const detail = partner?.userDetail;
    if (!detail) return;
    setForm(mapPartnerToDeliverySettingsForm(detail));
  }, [partner?.userDetail]);

  const updateField = useCallback(
    <K extends keyof DeliverySettingsForm>(
      key: K,
      value: DeliverySettingsForm[K]
    ) => {
      setForm((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const validatePrimeForFreeShipping = useCallback(async () => {
    if (!form.localFreeEnabled && !form.nationalFreeEnabled) return true;
    const plan = await fetchLastPremiumPlan({ token });
    return !!plan?.is_active;
  }, [form.localFreeEnabled, form.nationalFreeEnabled, token]);

  const handleSave = useCallback(async () => {
    if (!partner?.userDetail || !token) return;
    setLoading(true);
    try {
      const hasPrime = await validatePrimeForFreeShipping();
      if (!hasPrime) {
        toast.error(t("delivery.settings.primeRequired"));
        setLoading(false);
        return;
      }

      const freeConfig: FreeDeliveryConfigurationV2 = {
        localLogisticEnabled: form.localFreeEnabled,
        localOrderMinAmountValue: form.localFreeMin,
        nationalLogisticEnabled: form.nationalFreeEnabled,
        nationalOrderMinAmountValue: form.nationalFreeMin,
      };

      const logisticConfiguration: LogisticConfiguration =
        mergeLogisticConfiguration(partner.userDetail.logisticConfiguration, {
          customDeliveryEnabled: form.customDeliveryEnabled,
          customDeliveryValue: form.customDeliveryValue,
          nationalLogisticEnabled: form.nationalLogisticEnabled,
          nationalDeliveryValue: form.nationalDeliveryValue,
          freeDeliveryConfigurationV2: freeConfig,
          estimatedDeliveryTimeId: form.estimatedDeliveryTimeId || undefined,
          nationalEstimatedDeliveryTimeId:
            form.nationalEstimatedDeliveryTimeId || undefined,
        });

      const payload: Partner = {
        ...partner.userDetail,
        storeDelivery: form.storeDelivery,
        homeDelivery:
          form.customDeliveryEnabled ||
          form.nationalLogisticEnabled,
        logisticConfiguration,
      };

      const updated = await fetchUpdateUserDetailForDelivery(token, payload);
      const mergedUserDetail = mergeUserDetailAfterSave(
        partner.userDetail,
        payload,
        updated
      );
      dispatch(actions.setUserDetailData({ userDetail: mergedUserDetail }));
      setForm(mapPartnerToDeliverySettingsForm(mergedUserDetail));
      toast.success(t("delivery.settings.saveSuccess"));
    } catch {
      toast.error(t("delivery.settings.saveError"));
    } finally {
      setLoading(false);
    }
  }, [partner, token, form, dispatch, t, validatePrimeForFreeShipping]);

  const goToCoverage = useCallback(() => {
    if (userId) navigate(`/${userId}/delivery/${DELIVERY_ROUTES.COVERAGE}`);
  }, [navigate, userId]);

  const goToProfile = useCallback(() => {
    if (userId) navigate(`/${userId}/${PROFILE_ROUTES?.ROOT || "profile"}`);
  }, [navigate, userId]);

  const breadcrumbs = useMemo(
    () => [
      { label: t("delivery.breadcrumb.delivery"), link: `/${userId}/delivery` },
      { label: t("delivery.breadcrumb.settings") },
    ],
    [t, userId]
  );

  return {
    form,
    updateField,
    estimatedTimes,
    loading,
    handleSave,
    goToCoverage,
    goToProfile,
    breadcrumbs,
    userId,
  };
};

// Avoid circular import - profile route constant
const PROFILE_ROUTES = { ROOT: "profile" };
